import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import './Hero.css'

const Hero = () => {
  const [text, setText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [loopNum, setLoopNum] = useState(0)
  const typingSpeedRef = useRef(80)

  const roles = [
    'Full Stack Developer',
    'MERN Stack Specialist',
    'React.js Expert',
    'Node.js Developer',
    'UI/UX Enthusiast'
  ]

  const handleTyping = useCallback(() => {
    const i = loopNum % roles.length
    const fullText = roles[i]

    setText(
      isDeleting
        ? fullText.substring(0, text.length - 1)
        : fullText.substring(0, text.length + 1)
    )

    typingSpeedRef.current = isDeleting ? 40 : 80

    if (!isDeleting && text === fullText) {
      setTimeout(() => setIsDeleting(true), 1200)
    } else if (isDeleting && text === '') {
      setIsDeleting(false)
      setLoopNum(loopNum + 1)
    }
  }, [text, isDeleting, loopNum, roles])

  useEffect(() => {
    const timer = setTimeout(handleTyping, typingSpeedRef.current)
    return () => clearTimeout(timer)
  }, [handleTyping])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  }

  return (
    <section id="home" className="hero">
      <div className="hero-background">
        <div className="grid-pattern"></div>
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="hero-badge" variants={itemVariants}>
            <span className="badge-icon">👋</span>
            <span>Welcome to my portfolio</span>
          </motion.div>

          <motion.h1 className="hero-title" variants={itemVariants}>
            Hi, I'm <span className="text-gradient">Marvellous Onyema</span>
          </motion.h1>

          <motion.div className="hero-subtitle" variants={itemVariants}>
            <span className="typing-text">{text}</span>
            <span className="cursor">|</span>
          </motion.div>

          <motion.p className="hero-description" variants={itemVariants}>
            A passionate Full Stack Developer specializing in building exceptional digital experiences.
            I transform ideas into elegant, scalable, and user-friendly applications using modern technologies.
          </motion.p>

          <motion.div className="hero-cta" variants={itemVariants}>
            <Link to="/projects" className="btn btn-primary glow">
              <span>View My Work</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              <span>Get In Touch</span>
            </Link>
          </motion.div>

          <motion.div className="hero-stats" variants={itemVariants}>
            <div className="stat-item">
              <span className="stat-number">3+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">20+</span>
              <span className="stat-label">Projects Completed</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">15+</span>
              <span className="stat-label">Happy Clients</span>
            </div>
          </motion.div>

          <motion.div className="hero-scroll" variants={itemVariants}>
            <Link to="/about" className="scroll-indicator">
              <span>Scroll Down</span>
              <div className="scroll-arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M19 12l-7 7-7-7"/>
                </svg>
              </div>
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.div
            className="profile-picture-container"
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <img
              src="/portfolio.webp"
              alt="Onyema Marvellous - Full Stack Developer"
              className="profile-picture"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
